import React, { useEffect, useState } from "react";
import { Grid, Button, Snackbar, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import CircularLoader from "../../components/loader";
import HomeHeader from "../../components/HomeHeader";
import axios from "axios";

function TopQuotes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  // Add a refresh trigger state
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const headerTitle = location?.state;
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      navigate("/login");
    }
  }, [token, navigate]);

  // Modified useEffect to include refreshTrigger dependency
  useEffect(() => {
    const fetchQuotes = async () => {
      setLoading(true);
      try {
        const response = await axios.get("https://quotemanagement-497524552274.us-central1.run.app/quotes/unpaid", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        setQuotes(response.data.map((quote) => ({ ...quote, disabled: false }))); // Add 'disabled' flag
      } catch (err) {
        console.error("Failed to fetch quotes:", err);
        setError("Failed to fetch quotes. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, [token, refreshTrigger]); // Add refreshTrigger as a dependency

  const handleAction = async (quoteId, jobId, action) => {
    try {
      // Updated URL format to match the required endpoint structure
      const url = `https://quotemanagement-497524552274.us-central1.run.app/quotes/homeowner/${jobId}/${action}/${quoteId}`;

      // Make the API call to update the quote status
      await axios.post(url, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Show success message
      setSuccessMessage(`Quote ${action.toUpperCase()}ED successfully!`);
      setOpenSnackbar(true);
      setError(null);

      // Temporarily update the UI to show action is taking place
      setQuotes((prevQuotes) => 
        prevQuotes.map((quote) => {
          if (quote.id === quoteId) {
            // This is the quote we acted upon - update its status
            return { ...quote, status: action.toUpperCase(), disabled: false };
          } else if (quote.jobId === jobId) {
            // This is another quote for the same job - disable it
            return { ...quote, disabled: true };
          } else {
            // This quote is for a different job - leave it unchanged
            return quote;
          }
        })
      );
      
      // Trigger refresh after a short delay to allow the server to process the request
      setTimeout(() => {
        setRefreshTrigger(prev => prev + 1);
      }, 500);
      
    } catch (err) {
      console.error(`Failed to ${action} quote:`, err);
      setError(`Failed to ${action} quote. Please try again.`);
      setOpenSnackbar(true);
    }
  };

  const pendingQuotes = quotes.filter(q => !q.status || q.status === "PENDING");
  const acceptedQuotes = quotes.filter(q => q.status === "ACCEPTED");
  const rejectedQuotes = quotes.filter(q => q.status === "REJECTED");

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setError(null); // Clear error state when snackbar closes
  };

  return (
    <>
      {loading && <CircularLoader position="loaderMiddle" />}
      
      <Grid margin="5%" bgcolor="#FFFFFF">
        <Grid pb={1} display="flex" alignItems="center">
          <HomeHeader header={headerTitle || "Top Quotes"} />
        </Grid>

        {/* PENDING QUOTES */}
        <Grid mt={4}>
          <h3>Pending Quotes</h3>
          {pendingQuotes.length > 0 ? (
            <TableContainer component={Paper} sx={{ mb: 4 }}>
              <Table aria-label="pending quotes table">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Quote ID</strong></TableCell>
                    <TableCell><strong>Job ID</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingQuotes.map((quote) => (
                    <TableRow
                      key={quote.id}
                      sx={{ 
                        opacity: quote.disabled ? 0.5 : 1,
                        backgroundColor: quote.disabled ? "#f5f5f5" : "inherit",
                        '&:last-child td, &:last-child th': { border: 0 }
                      }}
                    >
                      <TableCell>{quote.id}</TableCell>
                      <TableCell>{quote.jobId}</TableCell>
                      <TableCell>R {quote.totalCost}</TableCell>
                      <TableCell>{quote.description}</TableCell>
                      <TableCell>{quote.status || "Pending"}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => handleAction(quote.id, quote.jobId, "accept")}
                          style={{ marginRight: "10px" }}
                          disabled={quote.disabled}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleAction(quote.id, quote.jobId, "reject")}
                          disabled={quote.disabled}
                        >
                          Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <p>No pending quotes.</p>
          )}
        </Grid>

        {/* ACCEPTED QUOTES */}
        <Grid mt={4}>
          <h3>Accepted Quotes</h3>
          {acceptedQuotes.length > 0 ? (
            <TableContainer component={Paper} sx={{ mb: 4, border: '1px solid green' }}>
              <Table aria-label="accepted quotes table">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Quote ID</strong></TableCell>
                    <TableCell><strong>Job ID</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {acceptedQuotes.map((quote) => (
                    <TableRow
                      key={quote.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>{quote.id}</TableCell>
                      <TableCell>{quote.jobId}</TableCell>
                      <TableCell>R {quote.totalCost}</TableCell>
                      <TableCell>{quote.description}</TableCell>
                      <TableCell>✅ Accepted</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <p>No accepted quotes.</p>
          )}
        </Grid>

        {/* REJECTED QUOTES */}
        <Grid mt={4}>
          <h3>Rejected Quotes</h3>
          {rejectedQuotes.length > 0 ? (
            <TableContainer component={Paper} sx={{ mb: 4, border: '1px solid red' }}>
              <Table aria-label="rejected quotes table">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Quote ID</strong></TableCell>
                    <TableCell><strong>Job ID</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rejectedQuotes.map((quote) => (
                    <TableRow
                      key={quote.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>{quote.id}</TableCell>
                      <TableCell>{quote.jobId}</TableCell>
                      <TableCell>R {quote.totalCost || quote.total}</TableCell>
                      <TableCell>{quote.description}</TableCell>
                      <TableCell>❌ Rejected</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <p>No rejected quotes.</p>
          )}
        </Grid>

        {/* Manual refresh button */}
        <Grid mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={() => setRefreshTrigger(prev => prev + 1)}
            disabled={loading}
          >
            Refresh Quotes
          </Button>
        </Grid>
      </Grid>

      {/* Snackbar for messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? "error" : "success"} 
          sx={{ width: "100%" }}
        >
          {error || successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TopQuotes;